/**
 * MemberFilterMultiSelect
 *
 * Searchable, clearable multi-select that filters by workspace member. Stores an
 * array of `userId` strings and matches typed text against the member's display
 * name AND email (case-insensitive substring), so typing part of an email finds
 * the member even when only the display name is shown.
 *
 * Wraps the shared `CollapsingMultiSelect` and reuses `useWorkspaceMemberSuggestions`
 * for the member list plus `MemberOptionRow` for the avatar + name option UI, so
 * the same control backs both the tasks "assignee" filter and the notes "author"
 * filter without deep-importing either feature's internals.
 *
 * @module components/common/MemberFilterMultiSelect
 */

import { memo } from 'react';
import { IconUser } from '@tabler/icons-react';
import {
  useMemberComboboxOptions,
  useWorkspaceMemberSuggestions,
} from '@/hooks';
import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { CollapsingMultiSelect } from '@/components/ui/CollapsingMultiSelect';

interface MemberFilterMultiSelectProps {
  readonly value: string[];
  readonly onChange: (ids: string[]) => void;
  readonly label: string;
  readonly placeholder: string;
  readonly className?: string;
}

function MemberFilterMultiSelectComponent({
  value,
  onChange,
  label,
  placeholder,
  className,
}: MemberFilterMultiSelectProps) {
  const { members } = useWorkspaceMemberSuggestions();
  const { options, renderOption, filter } = useMemberComboboxOptions(members);

  return (
    <CollapsingMultiSelect
      className={className}
      label={label}
      placeholder={placeholder}
      data={options}
      value={value}
      onChange={onChange}
      renderOption={renderOption}
      filter={filter}
      leftSection={<IconUser size={ICON_SIZE_SM} />}
      searchable
      clearable
      comboboxProps={{
        zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
        withinPortal: true,
      }}
    />
  );
}

export const MemberFilterMultiSelect = memo(MemberFilterMultiSelectComponent);
